import notification from "antd/es/notification";
import { defineMessages } from "react-intl";
import { put, select } from "redux-saga/effects";

import PersistentLeadFinderSettings from "@mapmycustomers/shared/types/persistent/PersistentLeadFinderSettings";

import {
  getLeadFinderConfiguration,
  getLeadFinderSettings,
  isConfigurationVisible,
} from "@app/component/leadFinder/store/selectors";
import { LEAD_FINDER_MAX_SEARCHES } from "@app/component/leadFinder/utils/consts";
import i18nService from "@app/config/I18nService";
import { handleError } from "@app/store/errors/actions";
import { isLeadFinderEnabled, isLeadFinderSaveEnabled } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";

import {
  clearLeadFinderLocation,
  hideLeadFinder,
  removeSearchHistoryItem,
  saveSearch,
  setConfigurationVisibility,
  setCustomSearchDistances,
  setDistanceSettings,
  setSearchDistance,
  setSearchHistory,
  showLeadFinder,
  showSavedSearchesModal,
} from "../actions";

const messages = defineMessages({
  saved: {
    id: "leadFinder.store.notifications.settings.saveNotification",
    defaultMessage: "Search saved",
    description: "Search saved - notification title",
  },
  savedDescription: {
    id: "leadFinder.store.notifications.settings.saveNotificationDescription",
    defaultMessage: "You can access it in the future under “Quick Search” when using Lead Finder",
    description: "Search saved - notification description",
  },
});

export function* onRemoveSearchHistoryItem({
  payload,
}: ReturnType<typeof removeSearchHistoryItem>) {
  try {
    const configuration: PersistentLeadFinderSettings = yield select(getLeadFinderConfiguration);

    const savedSearchesEnabled: boolean = yield select(isLeadFinderSaveEnabled);
    if (!savedSearchesEnabled) {
      const history: PersistentLeadFinderSettings = {
        ...configuration,
        savedSearches: [],
      };
      yield put(setSearchHistory(history));
      return;
    }

    const history: PersistentLeadFinderSettings = {
      ...configuration,
      recentSearches: (configuration.recentSearches ?? [])
        .filter((search) => search !== payload.search)
        .slice(0, LEAD_FINDER_MAX_SEARCHES - 1),
      savedSearches: (configuration.savedSearches ?? [])
        .filter((search) => search !== payload.search)
        .slice(0, LEAD_FINDER_MAX_SEARCHES - 1),
    };
    yield put(setSearchHistory(history));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onSaveSearch({ payload }: ReturnType<typeof saveSearch>) {
  try {
    const configuration: PersistentLeadFinderSettings = yield select(getLeadFinderConfiguration);

    const savedSearchesEnabled: boolean = yield select(isLeadFinderSaveEnabled);
    if (!savedSearchesEnabled) {
      const history: PersistentLeadFinderSettings = {
        ...configuration,
        savedSearches: [],
      };
      yield put(setSearchHistory(history));
      return;
    }

    if (
      Array.isArray(configuration.savedSearches) &&
      configuration.savedSearches.length >= LEAD_FINDER_MAX_SEARCHES
    ) {
      yield put(showSavedSearchesModal());
      return;
    }

    const history: PersistentLeadFinderSettings = {
      ...configuration,
      recentSearches: (configuration.recentSearches ?? [])
        .filter((search) => search !== payload.search)
        .slice(0, LEAD_FINDER_MAX_SEARCHES - 1),
      savedSearches: [
        payload.search,
        ...(configuration.savedSearches ?? [])
          .filter((search) => search !== payload.search)
          .slice(0, LEAD_FINDER_MAX_SEARCHES - 1),
      ],
    };
    yield put(setSearchHistory(history));

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        description: intl.formatMessage(messages.savedDescription),
        message: intl.formatMessage(messages.saved),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onSaveSearchHistory({ payload }: ReturnType<typeof setSearchHistory>) {
  try {
    const history: PersistentLeadFinderSettings = yield select(getLeadFinderConfiguration);
    yield put(
      updateMetadata.request({
        leadFinderSettings: {
          ...history,
          ...payload,
        },
      })
    );
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onSetDistanceSettings({
  payload: { customDistances, searchDistance },
}: ReturnType<typeof setDistanceSettings>) {
  try {
    if (Array.isArray(customDistances)) {
      yield put(setCustomSearchDistances({ customDistances }));
    }

    if (searchDistance !== undefined) {
      yield put(setSearchDistance({ distance: searchDistance }));
    }

    const leadFinderSettings: PersistentLeadFinderSettings = yield select(
      getLeadFinderConfiguration
    );

    yield put(
      updateMetadata.request({
        leadFinderSettings,
      })
    );
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onShowLeadFinder() {
  try {
    const isEnabled: boolean = yield select(isLeadFinderEnabled);
    yield put(setConfigurationVisibility({ visible: isEnabled }));

    if (isEnabled) {
      const savedSearchesEnabled: boolean = yield select(isLeadFinderSaveEnabled);
      const configuration: PersistentLeadFinderSettings = yield select(getLeadFinderSettings);
      const modifiedConfiguration = {
        ...configuration,
        savedSearches: savedSearchesEnabled ? configuration.savedSearches : [],
      };
      yield put(setSearchHistory(modifiedConfiguration));

      if (configuration.searchDistance && !isNaN(configuration.searchDistance)) {
        yield put(setSearchDistance({ distance: configuration.searchDistance }));
      }
      if (Array.isArray(configuration.customDistances)) {
        yield put(setCustomSearchDistances({ customDistances: configuration.customDistances }));
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onHideLeadFinder() {
  try {
    yield put(clearLeadFinderLocation());
    yield put(setConfigurationVisibility({ visible: false }));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onToggleLeadFinder() {
  try {
    const visible: boolean = yield select(isConfigurationVisible);
    yield put(visible ? hideLeadFinder() : showLeadFinder());
  } catch (error) {
    yield put(handleError({ error }));
  }
}
